const SALT_SIZE = 16;
const NONCE_SIZE = 12;
const KEY_LEN_BITS = 256;
const ITERATIONS = 100000;


function uint8ArrayToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}
/**
 * Helper untuk mengonversi Base64URL string kembali ke Uint8Array
 */
function base64UrlToUint8Array(base64Url: string): Uint8Array {
  let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) {
    base64 += '=';
  }
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

async function deriveKey(
  password: string,
  salt: Uint8Array,
  usage: KeyUsage
): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const passwordBytes = encoder.encode(password);

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    passwordBytes,
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt as unknown as BufferSource,
      iterations: ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: KEY_LEN_BITS },
    false,
    [usage]
  );
}

export async function encryptUrl(
  rawUrl: string,
): Promise<string> {
  // Bersihkan escape unicode \u0026 jika ada
  const cleanUrl = rawUrl.replace(/\\u0026/g, '&');
  const secret = process.env.NEXT_PUBLIC_KOCENG || "";

  const salt = crypto.getRandomValues(new Uint8Array(SALT_SIZE));
  const nonce = crypto.getRandomValues(new Uint8Array(NONCE_SIZE));

  const key = await deriveKey(secret, salt, 'encrypt');

  const encoder = new TextEncoder();
  const dataToEncrypt = encoder.encode(cleanUrl);

  const ciphertextBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: nonce },
    key,
    dataToEncrypt
  );

  const ciphertext = new Uint8Array(ciphertextBuffer);

  // Gabungkan payload: [ Salt (16B) | Nonce (12B) | Ciphertext ]
  const payload = new Uint8Array(SALT_SIZE + NONCE_SIZE + ciphertext.length);
  payload.set(salt, 0);
  payload.set(nonce, SALT_SIZE);
  payload.set(ciphertext, SALT_SIZE + NONCE_SIZE);

  return uint8ArrayToBase64Url(payload);
}

/**
 * Mendekripsi URL terenkripsi dari backend Golang.
 * 
 * @param encryptedBase64Url - String terenkripsi dari query parameter URL
 * @param secretPassword - (Opsional) Secret key. Default memakai process.env.KOCENG
 */
export async function decryptUrl(
  encryptedBase64Url: string,
  secretPassword?: string
): Promise<string> {
  const secret = secretPassword || process.env.NEXT_PUBLIC_KOCENG;

  if (!secret) {
    throw new Error('Secret key tidak ditemukan. Pastikan env KOCENG sudah diisi.');
  }

  try {
    const data = base64UrlToUint8Array(encryptedBase64Url);

    if (data.length < SALT_SIZE + NONCE_SIZE) {
      throw new Error('Payload terenkripsi tidak valid.');
    }

    // Pemisahan Salt (16B), Nonce/IV (12B), dan Ciphertext
    const salt = data.slice(0, SALT_SIZE);
    const nonce = data.slice(SALT_SIZE, SALT_SIZE + NONCE_SIZE);
    const ciphertext = data.slice(SALT_SIZE + NONCE_SIZE);

    // Derive Key dari Secret Key
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(secret),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    const key = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: ITERATIONS,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: KEY_LEN_BITS },
      false,
      ['decrypt']
    );

    // Proses Dekripsi AES-GCM
    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: nonce },
      key,
      ciphertext
    );

    return new TextDecoder().decode(decryptedBuffer);
  } catch (error) {
    console.error('Crypto error:', error);
    throw new Error('Gagal mendekripsi URL. Key tidak cocok atau data terdistorsi.');
  }
}